import {
  ChangeEvent,
  Dispatch,
  FocusEvent,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { Tooltip } from '../../../../../Atoms/Tooltip';
import { addCommasToNumber } from '../../../../../Utils/CommonUtils';
import { useWindowDimensions } from '../../../../../hooks/use-window-dimension';

export interface TEXT_INPUT_PROPS {
  warning?: string;
  warningText?: string;
  id: string;
  label: string;
  onTextChangeFn: (e: ChangeEvent<HTMLInputElement>, index?: number) => void;
  onBlurFn: (e: FocusEvent<HTMLInputElement>) => void;
  placeholder: string;
  value: string | number;
  showLabel?: boolean;
  type?: string;
  required?: boolean;
  prefix?: {
    enabled: boolean;
    icon: string | ReactNode;
  };
  suffix?: string;
  info?: {
    enabled: boolean;
    text: string;
  };
  min?: number;
  max?: number;
  disabled?: boolean;
  hidden?: boolean;
  hasError?: boolean;
  setGlobalError?: Dispatch<SetStateAction<boolean>>;
  index?: number;
  styles?: string;
  bottomMargin?: boolean;
  infoDisableOnDesktop?: boolean;
  labelClass?: string;
}

export const TextInput = ({
  warning,
  warningText,
  infoDisableOnDesktop = false,
  bottomMargin = true,
  id,
  label,
  onTextChangeFn,
  onBlurFn,
  placeholder,
  value,
  showLabel = false,
  type = 'text',
  required = false,
  prefix,
  suffix,
  info,
  min,
  max,
  disabled = false,
  hidden = false,
  hasError,
  setGlobalError,
  index = 0,
  styles,
  labelClass,
}: TEXT_INPUT_PROPS): JSX.Element => {
  const [minMaxError, setMinMaxError] = useState<string>('');
  const initialInputValue =
    value === 'undefined'
      ? ' '
      : type === 'text'
      ? addCommasToNumber(value)
      : value;
  const [inputValue, setInputValue] = useState(initialInputValue);

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (index) {
      onTextChangeFn(e, index);
    } else {
      setInputValue(e.target.value);

      if (type !== 'number') onTextChangeFn(e);
      if (type === 'text') {
        let num = e.target.value;
        num = num.replace(/[^0-9]/g, '');
        if (num.length > 3) {
          const lastThreeDigits = num.slice(-3);
          const remainingDigits = num.slice(0, -3);
          const formattedRemainingDigits = remainingDigits.replace(
            /\B(?=(\d{2})+(?!\d))/g,
            ','
          );
          const newCommaValue =
            formattedRemainingDigits + ',' + lastThreeDigits;
          setInputValue(newCommaValue);
        }
      }

      const newValue = Number(e.target.value);
      if (
        (min !== undefined && newValue < min) ||
        (max !== undefined && newValue > max)
      ) {
        setMinMaxError(`Value must be between ${min} and ${max}`);
        setGlobalError && setGlobalError(true);
      } else {
        setMinMaxError('');
        setGlobalError && setGlobalError(false);
      }
      onTextChangeFn(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Delete',
      'Tab',
    ];
    if (
      type === 'text' &&
      !/^[0-9]$/.test(e.key) &&
      !allowedKeys.includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (hasError && id === 'TaxableSalary') {
      setMinMaxError('This is mandatory field');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  const { width } = useWindowDimensions();
  const isDesktop = !!width && width >= 1024;

  return (
    <div
      className={`${bottomMargin ? 'mb-4 sm:mb-3' : ''} ${
        hidden ? 'hidden' : ''
      } ${styles}`}
    >
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className={`text-base text-font-500 mb-[6px] sm:mb-[4px] ${
            labelClass || 'font-medium'
          } ${showLabel ? '' : 'hidden'}`}
        >
          {label} {required && <span className="text-red-350">*</span>}
        </label>
        {info &&
        info.enabled === true &&
        !(infoDisableOnDesktop && isDesktop) ? (
          <Tooltip
            content={
              <div className="bg-[#041832] px-2 py-2 z-20 relative mt-1 rounded-sm flex justify-center text-white text-s-12 text-center sm:w-full sm:-translate-x-5">
                {info.text}
              </div>
            }
            hideCloseIcon={true}
            showOnHover={true}
            icon=""
            direction="bottom"
            contentPosition={'absolute'}
          >
            <img
              src="https://assets.clear.in/cleartax/images/1706006936_info.svg"
              alt=""
              width={24}
              height={24}
            />
          </Tooltip>
        ) : null}
      </div>

      <div className="flex relative">
        {prefix ? (
          <div className="px-4 sm:px-2 h-12 sm:h-9 flex items-center justify-center bg-grey-250 border border-grey-300 border-r-0 rounded-l-sm text-base sm:text-sm">
            {prefix.icon}
          </div>
        ) : null}
        <input
          id={id}
          type={type}
          className={`border border-grey-300 ${
            prefix?.enabled ? 'border-l-0 rounded-r-sm' : 'rounded-sm'
          } text-base sm:text-sm w-full h-12 sm:h-9 px-2 placeholder-gray-400 placeholder-opacity-75`}
          placeholder={`${placeholder} ${required ? '*' : ''}`}
          name={id}
          value={inputValue}
          onChange={handleTextChange}
          onBlur={onBlurFn}
          disabled={disabled}
          onKeyDown={handleKeyDown}
        />
        {suffix && (
          <span className="absolute top-1 right-1.5 py-2 h-10">{suffix}</span>
        )}
      </div>

      {minMaxError ? (
        <p className="text-red-350 text-s-14 font-medium inline-block">
          {minMaxError}
        </p>
      ) : null}
      {warning === 'yes' && minMaxError === '' ? (
        <p className="text-red-350 text-s-14 font-medium inline-block">
          {warningText}
        </p>
      ) : null}
    </div>
  );
};
